import { useCallback, useEffect } from 'react';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useLocation, useMatches, useNavigate, useNavigationType } from 'react-router-dom';
import { ROUTES } from '@cyberint/common-utils';

// Used only by one federation: ui-shell/old - so no harm
const pivotingState = atom([]);

export const useBreadCrumbs = ({ customer }) => {
  const matches = useMatches();
  const handledMatches = matches.filter(m => m.handle);

  const location = useLocation();
  const navigate = useNavigate();
  /** In case of REPLACE - replace last location without pushing new route */
  const navType = useNavigationType();
  const [pivots, setPivots] = useAtom(pivotingState);

  useEffect(() => {
    const match = matches.find(m => decodeURIComponent(m.pathname) === decodeURIComponent(location.pathname));
    let newList = [{ ...location, match }];
    const continuePivot = !location.state?.[ROUTES.DRILL_DOWN_STOP] && pivots.length > 1;
    /** Changing to startPivot will start regardless of DRILL_DOWN flag simply if this is any link */
    // const startPivot = !location.state?.[ROUTES.DRILL_DOWN_STOP];
    if (navType === 'POP') {
      /** POP when going back popes last element.
       * Initial rendering sends POP too but then nothing to remove */
      newList = pivots.length > 1 ? [...pivots.slice(0, pivots.length - 1)] : newList;
      /** P2: Need to differ from Forward POP - persist location(s) instead of popping elements and move all over the locations based on index */
    } else if (navType === 'REPLACE') {
      newList = [...pivots.slice(0, pivots.length - 1), { ...location, match }];
    } else if (location.state?.[ROUTES.DRILL_DOWN] || continuePivot /** || startPivot */) {
      const popCount = location.state?.pivotIndex;
      if (popCount < 0) {
        newList = [...pivots.slice(0, pivots.length + popCount)];
      } else {
        newList = [...pivots, { ...location, match }];
      }
    }
    setPivots(newList);
  }, [location, navType, matches]);

  /**
   * RAW THOUGHTS:
   * TBD - changing tenant will reset pivoting with last route/pivot (!) or empty list
   * but if last route/pivot is not visible to new customer, last saved pivot will be different from actual route
   * */
  useEffect(() => {
    if (pivots.length) setPivots(prev => [prev[prev.length - 1]]);
  }, [customer]);

  const closeBar = useCallback(() => {
    if (pivots?.length > 0) {
      const origin = pivots[0];
      setPivots([]);
      navigate(origin);
    }
  }, [pivots]);

  return { pivots, matches: handledMatches, closeBar };
};

export const usePivots = () => {
  const pivots = useAtomValue(pivotingState);
  return { pivots };
};
